import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBookVisit } from '@/context/BookVisitContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const propertyTypes = ['Plot', 'House', 'Commercial', 'Any'];
const locations = ['Shivpuri', 'Jamul', 'Bhilai', 'Khedamara', 'Any'];

export const BookVisitModal = () => {
  const { isOpen, propertyInfo, closeModal } = useBookVisit();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    property_type: '',
    preferred_location: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.mobile) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        property_id: propertyInfo?.id || null,
        property_title: propertyInfo?.title || null,
        source: propertyInfo ? 'property' : 'book-visit'
      });
      
      toast.success('Thank you! We will contact you soon.');
      setFormData({
        name: '',
        mobile: '',
        email: '',
        property_type: '',
        preferred_location: '',
        message: '',
      });
      closeModal();
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent className="sm:max-w-md" data-testid="book-visit-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {propertyInfo ? `Enquire about ${propertyInfo.title}` : 'Book a Site Visit'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your full name"
              required
              data-testid="book-visit-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="mobile">Mobile *</Label>
            <Input
              id="mobile"
              name="mobile"
              type="tel"
              value={formData.mobile}
              onChange={handleChange}
              placeholder="Your mobile number"
              required
              data-testid="book-visit-mobile-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Your email address"
              data-testid="book-visit-email-input"
            />
          </div>

          {!propertyInfo && (
            <>
              <div className="space-y-2">
                <Label>Property Type of Interest</Label>
                <Select value={formData.property_type} onValueChange={(v) => handleSelectChange('property_type', v)}>
                  <SelectTrigger data-testid="book-visit-type-select">
                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Preferred Location</Label>
                <Select value={formData.preferred_location} onValueChange={(v) => handleSelectChange('preferred_location', v)}>
                  <SelectTrigger data-testid="book-visit-location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Any specific requirements or questions?"
              rows={3}
              data-testid="book-visit-message-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-brand-orange to-brand-rust text-white rounded-full"
            data-testid="book-visit-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Enquiry'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
